import { FormControl, FormLabel, FormErrorMessage, Icon } from '@chakra-ui/react';

import {
  AsyncSelect,
  Select,
  SelectComponent,
  AsyncSelectComponent,
  Props,
  components,
  IndicatorsContainerProps,
  DropdownIndicatorProps,
} from 'chakra-react-select';

import { ReactElement, useCallback, useId } from 'react';
import {
  FieldPath,
  FieldValues,
  RegisterOptions,
  UseControllerProps,
  useController,
} from 'react-hook-form';
import { ChevronDown } from 'lucide-react';

export type SelectOption = {
  label: React.ReactNode;
  value: string;
};

function isSelectOption(value: unknown): value is SelectOption {
  return typeof value === 'object' && value !== null && 'label' in value && 'value' in value;
}

export type ControlledSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  Component?: React.ComponentType<Partial<Parameters<SelectComponent | AsyncSelectComponent>[0]>>;
  control: UseControllerProps<TFieldValues, TName>['control'];
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  name: TName;
  label: string | ReactElement;
  isRequired?: boolean;
  options?: SelectOption[];
  onChange?: (selected: SelectOption) => void;
  onInputChange?: Props['onInputChange'];
  isDisabled?: boolean;
  components?: Props['components'];
};

function IndicatorsContainer(props: IndicatorsContainerProps) {
  return (
    <div style={{ background: 'white' }}>
      <components.IndicatorsContainer {...props} />
    </div>
  );
}

function DropdownIndicator(props: DropdownIndicatorProps) {
  return (
    <components.DropdownIndicator {...props}>
      <Icon as={ChevronDown} color="fg.muted" mr={2} />
    </components.DropdownIndicator>
  );
}

/**
 * A component that wraps a chakra-react-select in react-hook-form.
 *
 * @component
 * @example
 * ```tsx
 * import { useForm } from 'react-hook-form';
 * import { ControlledSelect } from '@duke-hq/ui';
 *
 * function MyForm() {
 *   const { control, handleSubmit } = useForm();
 *
 *   const onSubmit = (data) => {
 *     console.log(data);
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit(onSubmit)}>
 *       <ControlledSelect
 *         control={control}
 *         name="mySelect"
 *         label="Select an option"
 *         options={[
 *           { label: 'Option 1', value: 'option1' },
 *           { label: 'Option 2', value: 'option2' },
 *           { label: 'Option 3', value: 'option3' },
 *         ]}
 *       />
 *       <button type="submit">Submit</button>
 *     </form>
 *   );
 * }
 * ```
 */
export function ControlledSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  Component = Select,
  control,
  name,
  label,
  isRequired,
  rules,
  options,
  onChange: propsOnChange,
  ...props
}: ControlledSelectProps<TFieldValues, TName>) {
  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { invalid, error },
  } = useController<TFieldValues, TName>({
    name,
    control,
    rules,
  });

  const fieldId = useId();

  const handleChange = useCallback(
    (newValue: unknown) => {
      if (
        newValue != null &&
        typeof newValue === 'object' &&
        'value' in newValue &&
        newValue.value != null &&
        (typeof newValue.value === 'string' ||
          typeof newValue.value === 'number' ||
          typeof newValue.value === 'boolean' ||
          typeof newValue.value === 'object')
      ) {
        onChange(newValue.value);
        if (typeof propsOnChange === 'function' && isSelectOption(newValue)) {
          propsOnChange(newValue);
        }
      }
    },
    [onChange, propsOnChange],
  );

  return (
    <FormControl
      isRequired={isRequired}
      isInvalid={invalid}
      opacity={props.isDisabled === true ? 0.5 : undefined}
    >
      <FormLabel htmlFor={fieldId} color="fg.emphasis" fontWeight="normal" fontSize="small" mb={1}>
        {label}
      </FormLabel>
      <Component
        inputId={fieldId}
        name={name}
        isDisabled={props.isDisabled}
        ref={ref}
        onBlur={onBlur}
        options={options}
        value={options && value ? options.find((option) => option.value === value) : null}
        onChange={handleChange}
        {...props}
        components={{
          IndicatorsContainer,
          DropdownIndicator,
          ...props.components,
        }}
      />

      <FormErrorMessage>{error?.message}</FormErrorMessage>
    </FormControl>
  );
}
/**
 * A convenience component that wraps ControlledSelect with AsyncSelect as the default component.
 */
export function ControlledAsyncSelect(props) {
  return ControlledSelect({
    Component: AsyncSelect,
    ...props,
  });
}
